import {
  useState, useRef, useMemo, useEffect
} from "react";
import { ReactGrid } from "@silevis/reactgrid";
import { Input } from "@hydra/atom/components";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { kebabCase } from "lodash";
import { useSelector } from "react-redux";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import { getDynamicObjectRecordById } from "@/api/dynamic/dynamicObjectNameApi";
import {
  TwoColumnList, BoxedContent, Header, SvgIcon, IconButton, HeaderLeftContent
} from "@/components/common";
import {
  getReceiptColumns, getReceiptRows, getTenantStatementRows, getTenantStatementColumns,
  getPdcColumns, getMemoRows
} from "./tenant-statement";
import {
  getTenantStatementInvoices, getTenantStatementLedger, getTenantStatementPdc
} from "@/api/finance/reportApi";
import { formatCurrency } from "@/utils/helpers";
import { renderDynamicFieldValue } from "@/utils/dynamic/helpers";
import {
  DynamicReport
} from "@/components/dynamic";
import { selectActiveCompany } from "@/store/appSlice";
import appSettings from "@/settings";

// The invoices section reports one row per rental installment and one per general invoice.
// Security deposit and utility are deliberately not rows of their own: they are already inside
// the installment's own Amount -- the invoice debits receivables by the installment total and
// credits rent, deposit, utility and admin charges out of it -- so listing them separately
// counted both twice. Only the rent rows were ever rendered, which is what hid that.
const INVOICE_GROUPS = [
  { key: "rent", title: "Rental Invoices" },
  { key: "other", title: "General Invoices" },
  { key: "credit", title: "Credit Notes Not Against An Invoice" },
];

function DetailItem({ heading, children }) {
  return (
    <div className="row">
      <div className="col-12">
        <h6 className="headline">{heading}</h6>
        {children}
      </div>
    </div>
  );
}

// A disclosure block beneath the statement. Deliberately its own grid: neither cheques on hand nor
// unapplied credit notes are receivable movements, so mixing them into the statement would show
// rows that do not add up to its total, and paging the statement would hide them on every page but
// the last.
function MemoBlock({
  title, note, columns, rows, totalLabel
}) {
  if (!rows?.length) {
    return null;
  }

  return (
    <div className="table-wrapper report-sheet memo-block">
      <div className="memo-block-heading">
        <h6>{title}</h6>
        <span className="memo-block-note">{note}</span>
      </div>
      <div className="react-grid-container">
        <ReactGrid
          rows={getMemoRows({ data: { data: rows }, columns, totalLabel })}
          columns={columns}
          enableRangeSelection
        />
      </div>
    </div>
  );
}

function ReportTenantStatement() {
  const activeCompany = useSelector(selectActiveCompany);
  const ref = useRef();
  const [filterText, setFilterText] = useState("");
  const params = useParams();
  const tenant = params?.id;
  const [tenantStatement, setTenantStatement] = useState({
    rent: [],
    other: [],
    credit: []
  });

  const exportExcel = (id) => window.open(`${appSettings.baseUrl}/leasing/reports/tenant/${id}/xls?companyId=${activeCompany.id}`, "_blank", "noopener,noreferrer");

  // AccountCode is left empty on purpose so the report resolves the tenant's own configured
  // receivable account rather than assuming every company posts receivables to one hardcoded
  // code. Picking an account in the filter drawer still overrides it.
  const tenantStatementLedgerFilters = {
    TenantId: tenant,
    AccountCode: "",
  };

  // CompanyId is sent explicitly so this grid and the statement ledger below are scoped to the
  // same company. Without it the report fell back to the HTTP context company while the ledger
  // used the active company, so the two totals were not answerable against each other. The query
  // key carries the tenant and company for the same reason: keyed on a bare string it served one
  // tenant's invoices while showing another's, and never refetched on a company switch.
  const { data: tenantStatementData } = useQuery(
    ["tenant-statement-invoices", tenant, activeCompany?.id],
    () => getTenantStatementInvoices({ TenantId: tenant, CompanyId: activeCompany?.id }),
    {
      enabled: Boolean(tenant) && Boolean(activeCompany?.id),
    }
  );

  const memoFilters = { TenantId: tenant, CompanyId: activeCompany?.id };
  const memoEnabled = Boolean(tenant) && Boolean(activeCompany?.id);

  const { data: pdcData } = useQuery(
    ["tenant-statement-pdc", tenant, activeCompany?.id],
    () => getTenantStatementPdc(memoFilters),
    { enabled: memoEnabled }
  );

  // The customer record itself, not the customer's most recent contract. The statement is drawn
  // for a customer, and reaching their name through a contract meant a customer with no contract
  // -- a General-Invoice-only customer -- rendered the whole header blank, which is exactly what
  // happened for tenant-1CXkzcQ5TKUEBcn3WQMLtB.
  const { data: tenantRecord } = useQuery(
    [kebabCase(dynamicObjectMap.get("TenantObjectName")), tenant],
    () => getDynamicObjectRecordById(dynamicObjectMap.get("TenantObjectName"), tenant),
    {
      enabled: Boolean(tenant),
    }
  );

  // /object/tenant/{id} answers with the record itself, not a {data} envelope like the list
  // endpoints -- and the shared `request` helper unwraps some responses and not others, so both
  // shapes turn up in this file. Accept either rather than guessing.
  const customer = useMemo(() => {
    const record = tenantRecord?.name ? tenantRecord : tenantRecord?.data;
    return record ?? {};
  }, [tenantRecord]);

  useEffect(() => {
    if (!tenantStatementData) {
      return;
    }

    let invoices = tenantStatementData.data;

    if (filterText) {
      const needle = filterText.toLowerCase();
      // Guarded because a general invoice with no number would otherwise throw here, and
      // general invoices only started reaching this grid once the OTHER group was rendered.
      invoices = invoices?.filter(
        (item) => (item.invoiceNumber || "").toLowerCase().includes(needle)
      );
    }

    const grouped = { rent: [], other: [], credit: [] };

    invoices?.forEach((item, index) => {
      const category = (item.category || "").toLowerCase();
      const bucket = grouped[category] ?? grouped.other;

      bucket.push({
        // ReactGrid needs a unique rowId, and a query report has no record key to offer.
        id: `${category || "other"}-${item.invoiceNumber || "na"}-${index}`,
        description: item.memo || "Invoice",
        invoiceNo: item.invoiceNumber,
        contractNumber: item.contractNumber,
        // The invoice's own date. `item.date` beside it is the installment due date, which is a
        // different thing and is not rendered.
        transactionDate: item.transactionDate,
        // Kept numeric: the column formatters render them and the group footers total them.
        amount: Number(item.amount) || 0,
        vat: Number(item.vAT) || 0,
        total: Number(item.total) || 0,

        // The three ways a line gets settled, kept apart rather than summed into one
        // Paid/Adjusted figure -- see the note above getTenantStatementColumns. Each amount is
        // paired with the documents behind it, which stay strings: they are lists, not values.
        paidAmount: Number(item.paidAmount) || 0,
        paidNumbers: item.paidNumbers || "",
        creditedAmount: Number(item.creditedAmount) || 0,
        creditNoteNumbers: item.creditNoteNumbers || "",
        // Cheques on hand against this invoice. Reported beside the balance, never inside it:
        // a collected cheque has not reduced the receivable in the ledger.
        pdcAmount: Number(item.pdcAmount) || 0,
        pdcNumbers: item.pdcNumbers || "",
        balance: Number(item.balance) || 0
      });
    });

    setTenantStatement(grouped);
  }, [tenantStatementData, filterText]);

  const columns = useMemo(
    () => getTenantStatementColumns(ref?.current?.clientWidth),
    [ref?.current?.clientWidth]
  );

  const pdcColumns = useMemo(() => getPdcColumns(), []);

  const pdcRows = useMemo(
    () => (pdcData?.data ?? []).map((item, index) => ({
      id: `pdc-${item.number || index}`,
      number: item.number,
      chequeNo: item.chequeNo,
      chequeDate: item.chequeDate,
      dueDate: item.dueDate,
      bank: item.bank,
      contractNumber: item.contractNumber,
      amount: Number(item.amount) || 0
    })),
    [pdcData]
  );

  const chequesOnHand = useMemo(
    () => pdcRows.reduce((sum, row) => sum + row.amount, 0),
    [pdcRows]
  );

  // The statement's closing balance, captured from the ledger grid so the net-of-cheques figure
  // can be derived. It is only trustworthy because that grid is unpaged -- on a paged statement
  // the last row on screen is not the last row of the statement.
  const [closingBalance, setClosingBalance] = useState(null);

  const onLedgerLoaded = (ledgerRows) => {
    const last = [...(ledgerRows ?? [])]
      .reverse()
      .find((row) => row.runningBalance !== null && row.runningBalance !== undefined);

    setClosingBalance(last ? Number(last.runningBalance) : null);
  };

  const rows = useMemo(
    () =>
      getTenantStatementRows({
        data: {
          data: tenantStatement,
        },
        columns,
        groups: INVOICE_GROUPS,
      }),
    [columns, tenantStatement]
  );

  return (
    <BoxedContent>
      <Header
        showBreadcrumb
        leftContent={<HeaderLeftContent title="Tenant Statement" icon="file-check-stroke-icon" />}
        rightContent={(
          <div>
            <IconButton
              className="icon-button-with-text"
              onClick={() => exportExcel(tenant)}
            >
              <SvgIcon icon="excel" />
              <span>Download Excel</span>
            </IconButton>
          </div>
        )}
      />
      <>
        <DetailItem>
          <div className="card">
            <div className="row">
              <div className="col">
                <TwoColumnList
                  data={[
                    {
                      label: "Customer Name",
                      value: customer?.name,
                    },
                    {
                      label: "Customer No.",
                      render: renderDynamicFieldValue({
                        fieldType: "Lookup",
                        fieldName: "tenant",
                        label: customer?.number,
                        value: customer?.number,
                        id: customer?.id,
                      })
                    },
                  ]}
                />
              </div>
              <div className="col">
                <TwoColumnList
                  data={[
                    {
                      label: "Email",
                      value: customer?.email,
                    },
                    {
                      label: "Mobile",
                      value: customer?.mobile,
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </DetailItem>
        <div className="table-wrapper report-sheet" ref={ref}>
          <div className="row filter-row">
            <div className="col-md-3">
              <Input
                className="input-height-fix"
                value={filterText}
                onChange={setFilterText}
                placeholder="Search by name"
              />
            </div>
          </div>
          <div className="react-grid-container">
            <ReactGrid
              rows={rows}
              columns={columns}
              stickyTopRows={1}
              enableFillHandle
              enableRangeSelection
            />
          </div>
        </div>
        {/*
          Unpaged on purpose. A statement of account is one document, not a list: paged at 50 rows
          each page rendered its own partial Total footer as though it were the statement total, and
          the closing balance -- which the net-of-cheques figure below is derived from -- was only
          ever on the last page.
        */}
        <DynamicReport
          reportName="tenant-statement"
          activeCompany={activeCompany}
          columnsDataProvider={getReceiptColumns}
          rowsDataProvider={getReceiptRows}
          fetchData={getTenantStatementLedger}
          exportUrl="reports/export-query-report/tenant-statement"
          filtersInitialState={tenantStatementLedgerFilters}
          filterOptions={[{ key: "accountCode", apiKey: "AccountCode" }]}
          orderBy=""
          isPaginated={false}
          onDataLoaded={onLedgerLoaded}
        />

        <MemoBlock
          title="Post-dated cheques on hand"
          note="Cheques collected and not yet cleared. Not included in the statement balance above."
          columns={pdcColumns}
          rows={pdcRows}
          totalLabel="Cheques on hand"
        />

        {/*
          Only shown when the customer is actually in arrears. Netting cheques against a credit
          balance reads as a huge prepayment, when in fact the cheques cover rent not yet invoiced
          -- a customer paying a year of PDCs up front showed "net (297,262.50)" and meant nothing
          by it. Against a debit balance the figure answers a real question: what is still exposed
          once the cheques we hold are counted.
        */}
        {pdcRows.length > 0 && closingBalance > 0 ? (
          <div className="memo-block memo-net-line">
            <span>Balance net of cheques on hand</span>
            <strong>{formatCurrency(String(closingBalance - chequesOnHand))}</strong>
          </div>
        ) : null}

      </>
    </BoxedContent>

  );
}

export default ReportTenantStatement;
